import type { AutomationPeopleSelection } from "@repo/app-types/entities/automation";
import type { GroupId } from "@repo/app-types/entities/group";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { Combobox } from "@repo/ui/combobox/combobox";
import { ComboboxChips } from "@repo/ui/combobox/comboboxChips";
import { ComboboxContent } from "@repo/ui/combobox/comboboxContent";
import { ComboboxItem } from "@repo/ui/combobox/comboboxItem";
import { ComboboxSection } from "@repo/ui/combobox/comboboxSection";
import { useMemo, type FC } from "react";
import type { GroupOption } from "./groupPicker.js";
import type { TeammateOption } from "./teammatePicker.js";

/** Sentinel option ids; teammate and group ids are uuids, so they can never collide. */
export const ALL_TEAMMATES_OPTION_ID = "all-teammates";
export const ALL_MANAGERS_OPTION_ID = "all-managers";

interface PeopleOption {
  id: string;
  label: string;
}

interface PeopleSection {
  id: string;
  label?: string;
  options: readonly PeopleOption[];
}

/** A recipient selection with group chips always available. */
export interface PeoplePickerSelection extends AutomationPeopleSelection {
  groupIds: readonly GroupId[];
}

export interface PeoplePickerOptions {
  teammateOptions: readonly TeammateOption[];
  managerTeammateIds: readonly TeammateId[];
  groupOptions: readonly GroupOption[];
}

export interface PeoplePickerProps extends PeoplePickerOptions {
  /** Accessible name for the combobox; the visible field label lives outside. */
  ariaLabel: string;
  value: PeoplePickerSelection;
  onChange: (value: PeoplePickerSelection) => void;
  /**
   * While the caller's options are still loading, disable the control and show a
   * loading placeholder. The picker only ever renders the options it's handed —
   * it never substitutes a fallback set — so this keeps an in-flight fetch from
   * looking like an empty (or wrong) list.
   */
  isLoading?: boolean;
  /** Renders the picker disabled, e.g. for read-only viewers. */
  isDisabled?: boolean;
}

/** The option ids a selection renders as, in chip order. */
export function listPeoplePickerSelectedIds(
  value: PeoplePickerSelection,
): string[] {
  return [
    ...(value.includesAllTeammates ? [ALL_TEAMMATES_OPTION_ID] : []),
    ...(value.includesAllManagers ? [ALL_MANAGERS_OPTION_ID] : []),
    ...value.teammateIds,
    ...value.groupIds,
  ];
}

/**
 * The selection after the combobox reports `keys`, given what was selected
 * before. Only the option just picked decides what else changes; group
 * membership is never consulted:
 * - picking a teammate clears "All teammates", picking a manager clears "All
 *   managers", and a pick from the other audience changes nothing;
 * - picking a group clears both "all" options when both were selected, and
 *   nothing when only one was;
 * - picking an "all" option removes the people it covers, and when it is the
 *   second "all" option it removes the groups too;
 * - deselecting anything restores nothing.
 * Ids are reported in options order, so a re-selection can't look like a
 * change to callers that compare the arrays.
 */
export function resolvePeoplePickerSelection(
  previous: PeoplePickerSelection,
  keys: readonly string[],
  options: PeoplePickerOptions,
): PeoplePickerSelection {
  const { teammateOptions, managerTeammateIds, groupOptions } = options;
  const nextIds = new Set(keys);
  const previousIds = new Set(listPeoplePickerSelectedIds(previous));
  const addedIds = new Set([...nextIds].filter((id) => !previousIds.has(id)));
  const managerIds = new Set(managerTeammateIds);
  const hasBothAudiences = () =>
    nextIds.has(ALL_TEAMMATES_OPTION_ID) && nextIds.has(ALL_MANAGERS_OPTION_ID);

  const pickedPeople = teammateOptions.filter(({ id }) => addedIds.has(id));
  if (pickedPeople.some(({ id }) => !managerIds.has(id))) {
    nextIds.delete(ALL_TEAMMATES_OPTION_ID);
  }
  if (pickedPeople.some(({ id }) => managerIds.has(id))) {
    nextIds.delete(ALL_MANAGERS_OPTION_ID);
  }
  if (groupOptions.some(({ id }) => addedIds.has(id)) && hasBothAudiences()) {
    nextIds.delete(ALL_TEAMMATES_OPTION_ID);
    nextIds.delete(ALL_MANAGERS_OPTION_ID);
  }

  const addedAllTeammates = addedIds.has(ALL_TEAMMATES_OPTION_ID);
  const addedAllManagers = addedIds.has(ALL_MANAGERS_OPTION_ID);
  for (const { id } of teammateOptions) {
    if (managerIds.has(id) ? addedAllManagers : addedAllTeammates) {
      nextIds.delete(id);
    }
  }
  if ((addedAllTeammates || addedAllManagers) && hasBothAudiences()) {
    for (const { id } of groupOptions) nextIds.delete(id);
  }

  return {
    includesAllTeammates: nextIds.has(ALL_TEAMMATES_OPTION_ID),
    includesAllManagers: nextIds.has(ALL_MANAGERS_OPTION_ID),
    teammateIds: teammateOptions
      .filter(({ id }) => nextIds.has(id))
      .map(({ id }) => id),
    groupIds: groupOptions
      .filter(({ id }) => nextIds.has(id))
      .map(({ id }) => id),
  };
}

/**
 * The recipients people picker: one multi-select of the "All teammates" and
 * "All managers" options, then teammate groups, teammates and managers as
 * sections, all freely combinable. Whether a picked person gets a teammate
 * result or manager treatment is not chosen here — the save path and the run
 * partition the selection by who manages a group, and a group's members are
 * partitioned the same way at plan time.
 */
export const PeoplePicker: FC<PeoplePickerProps> = (props) => {
  const {
    ariaLabel,
    teammateOptions,
    managerTeammateIds,
    groupOptions,
    value,
    onChange,
    isLoading,
    isDisabled,
  } = props;

  const sections = useMemo<readonly PeopleSection[]>(() => {
    const managerIds = new Set(managerTeammateIds);
    const allSections: readonly PeopleSection[] = [
      {
        id: "all",
        options: [
          { id: ALL_TEAMMATES_OPTION_ID, label: "All teammates" },
          { id: ALL_MANAGERS_OPTION_ID, label: "All managers" },
        ],
      },
      { id: "groups", label: "Teammate groups", options: groupOptions },
      {
        id: "teammates",
        label: "Teammates",
        options: teammateOptions.filter(({ id }) => !managerIds.has(id)),
      },
      {
        id: "managers",
        label: "Managers",
        options: teammateOptions.filter(({ id }) => managerIds.has(id)),
      },
    ];
    // Typing already drops sections left without matches; the unfiltered menu
    // would otherwise still show a header over nothing.
    return allSections.filter((section) => section.options.length > 0);
  }, [teammateOptions, managerTeammateIds, groupOptions]);

  const selectedIds = useMemo(
    () => listPeoplePickerSelectedIds(value),
    [value],
  );

  return (
    <div className="w-full sm:max-w-md">
      <Combobox<PeopleSection, "multiple">
        aria-label={ariaLabel}
        selectionMode="multiple"
        defaultItems={sections}
        value={selectedIds}
        onChange={(keys) =>
          onChange(
            resolvePeoplePickerSelection(value, keys.map(String), {
              teammateOptions,
              managerTeammateIds,
              groupOptions,
            }),
          )
        }
        isDisabled={isLoading || isDisabled}
      >
        <ComboboxChips<PeopleOption>
          placeholder={
            isLoading ? "Loading…" : "Select teammates, managers or groups"
          }
          chipsLabel={`Selected ${ariaLabel.toLowerCase()}`}
        >
          {(option) => option.label}
        </ComboboxChips>
        <ComboboxContent<PeopleSection> maxHeight={320}>
          {(section) => (
            <ComboboxSection
              id={section.id}
              label={section.label}
              items={section.options}
            >
              {(option) => (
                <ComboboxItem id={option.id}>{option.label}</ComboboxItem>
              )}
            </ComboboxSection>
          )}
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
